import {
  either,
  equals,
  filter,
  find,
  identity,
  isEmpty,
  isNil,
  pick,
  pickAll,
  pipe,
  reject,
  whereEq,
} from 'ramda'
import { allKey } from 'app/constants'
import { AppSelector, RootState } from 'app/store'
import {
  arrayIfEmpty,
  arrayIfNil,
  arrayIfNilOrEmpty,
  emptyArr,
  ensureArray,
  isNilOrEmpty,
  paramsCartesianProduct,
} from 'app/utils/fp'
import { dataStoreKey, ParamsType, paramsStoreKey } from 'core/caching/cacheReducers'
import { GlobalParams } from 'core/hooks/useGlobalParams'
import { getUserPrefsBucket, preferencesStoreKey } from 'core/session/preferencesReducers'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { createSharedSelector, selectParamsFromProps } from 'core/utils/selectorHelpers'
import { IDataKeys } from 'k8s/datakeys.model'
import { generateObjMemoizer, memoize, memoizedObj, memoizeShallow } from 'utils/misc'

interface SelectorParams {
  useGlobalParams?: boolean
}

const selectParams = selectParamsFromProps<SelectorParams>()
const arrayizeIndexBy = (args: [string, string | string[]]) => {
  const [dataKey, indexBy = emptyArr]: [string, string[] | string] = args
  return [dataKey, ensureArray(indexBy)]
}

const memoizeCombinations = memoize((params) => paramsCartesianProduct(params))

/**
 * Memoized function to convert normalized state { ids, entities } to array
 * This prevents creating new array references on every selector call, which would
 * break memoization and cause infinite loops
 */
const memoizeNormalizedToArray = memoizeShallow((normalized: any) => {
  if (!normalized) return emptyArr
  // Already an array
  if (Array.isArray(normalized)) return normalized
  // Is normalized format - convert to array
  if (normalized.ids && normalized.entities) {
    return normalized.ids.map((id: any) => normalized.entities[id]).filter(Boolean)
  }
  return emptyArr
})

const findIndexedParamsItem = (params) => {
  const memoizedParams = memoizedObj(params)
  const combinations = memoizeCombinations(memoizedParams)
  return (item) => {
    return combinations.some((combination) => {
      return whereEq(combination)(item)
    })
  }
}

const findIndexedParamsCollection = (params) => {
  const memoizedParams = memoizedObj(params)
  const combinations = memoizeCombinations(memoizedParams)
  return (storeData) => {
    return combinations.some((combination) => {
      return !!find(whereEq(combination))(storeData)
    })
  }
}

const getDataSelector = memoizeShallow(
  <T extends keyof IDataKeys>(
    dataKey: T,
    indexBy: string | string[] = emptyArr,
    globalParamKeys?: Array<keyof GlobalParams>,
  ) => {
    const globalParamKeysMemo = generateObjMemoizer<Partial<GlobalParams>>()
    const selectGlobalParams = (state: RootState): IDataKeys[T] => {
      if (!globalParamKeys) {
        return null
      }
      const { username } = state[sessionStoreKey]
      const userPrefs = getUserPrefsBucket(state[preferencesStoreKey], username)
      return globalParamKeysMemo(pick(globalParamKeys, userPrefs?.globalParams || {}))
    }
    const selectAllData = (state: RootState): IDataKeys[T] => {
      const cached = state.cache[dataStoreKey][dataKey]
      if (!cached) return arrayIfNilOrEmpty(null)

      // Use memoized conversion to prevent creating new array references
      // This is critical for preventing infinite loops caused by selector invalidation
      return arrayIfNilOrEmpty(memoizeNormalizedToArray(cached))
    }
    const selectAllParams = (state: RootState): ParamsType[] =>
      arrayIfNilOrEmpty(state.cache[paramsStoreKey][dataKey])
    const dataSelector: AppSelector<IDataKeys[T]> = createSharedSelector(
      selectAllData,
      selectAllParams,
      selectParams,
      selectGlobalParams,
      (storeData, storeParams, params, globalParams) => {
        // set ignoreGlobalParams false only if explicitly set false
        const ignoreGlobalParams = params?.useGlobalParams === false
        const providedIndexedParams = pipe<ParamsType, ParamsType, ParamsType>(
          pickAll(ensureArray(indexBy)),
          reject(either(isNil, equals(allKey))),
        )(params as ParamsType)
        // If the provided params are already cached
        // Search either for the provided params or an empty object (it means we fetched for ALL items)
        if (
          isNilOrEmpty(indexBy) ||
          findIndexedParamsCollection(providedIndexedParams)(storeData)
        ) {
          const filterByParams = isEmpty(providedIndexedParams)
            ? identity
            : filter(findIndexedParamsItem(providedIndexedParams))
          const filterByGlobalParams =
            !globalParams || ignoreGlobalParams
              ? identity
              : (data) =>
                  globalParamKeys.reduce((accFilteredData, globalParamKey) => {
                    const globalParamValue = globalParams[globalParamKey]
                    if (isNilOrEmpty(globalParamValue) || globalParamValue === allKey) {
                      return accFilteredData
                    }
                    return accFilteredData.filter((row) =>
                      Array.isArray(globalParamValue)
                        ? globalParamValue.includes(row[globalParamKey])
                        : globalParamValue === row[globalParamKey],
                    )
                  }, data)

          // Return the cached data filtering by the provided params
          return pipe<
            IDataKeys[T] | [],
            IDataKeys[T] | [],
            IDataKeys[T] | [],
            IDataKeys[T] | [],
            IDataKeys[T] | []
          >(
            arrayIfNil,
            // Filter the data by the provided params
            filterByParams,
            filterByGlobalParams,
            // Return the constant emptyArr to avoid unnecessary re-renderings
            arrayIfEmpty,
          )(storeData)
        }
        return emptyArr
      },
    )
    return dataSelector
  },
  { transformArgs: arrayizeIndexBy },
)

/**
 * Optimized selector for direct O(1) lookups by ID from normalized cache
 * Use this when you need to fetch a single item by ID instead of filtering the entire list
 *
 * @example
 * const selectRouterById = getByIdSelector(DataKeys.OpenstackRouters)
 * const router = useAppSelector(state => selectRouterById(state)('router-1'))
 */
export const getByIdSelector = memoizeShallow(<T extends keyof IDataKeys>(dataKey: T) => {
  return createSharedSelector(
    (state: RootState) => state.cache[dataStoreKey][dataKey],
    (cached) => {
      // Return a function that performs O(1) lookup
      return (id: string | number) => {
        if (!cached) return null

        // Handle normalized format: { ids: [], entities: {} }
        if (cached.ids && cached.entities) {
          return cached.entities[id] || null
        }

        // Fallback: handle array format for backward compatibility
        if (Array.isArray(cached)) {
          return cached.find((item: any) => item?.id === id) || null
        }

        return null
      }
    },
  )
})

/**
 * Returns the entities object directly from normalized cache for direct property access
 * Use this when you need to access multiple items by ID: entitiesById[id]
 *
 * @example
 * const usersById = getEntitiesByIdSelector(DataKeys.ManagementUsers)
 * const users = useAppSelector(usersById)
 * const user = users[userId] // Direct O(1) access
 * const anotherUser = users[anotherUserId] // Another O(1) access
 */
export const getEntitiesByIdSelector = memoizeShallow(<T extends keyof IDataKeys>(dataKey: T) => {
  return createSharedSelector(
    (state: RootState) => state.cache[dataStoreKey][dataKey],
    (cached) => {
      if (!cached) return {}

      // Handle normalized format: { ids: [], entities: {} }
      if (cached.ids && cached.entities) {
        return cached.entities
      }

      // Fallback: convert array to object map for backward compatibility
      if (Array.isArray(cached)) {
        return cached.reduce(
          (acc, item) => {
            if (item?.id) {
              acc[item.id] = item
            }
            return acc
          },
          {} as Record<string | number, any>,
        )
      }

      return {}
    },
  )
})

export default getDataSelector
